import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, ChevronDown, Loader2 } from "lucide-react";
import { API, formatApiError } from "../auth/api";

/**
 * 2026-10-28: "Kopiér kalenderne til andre pladser" hos logistikfirmaet (egne kunder med pladser,
 * egne lokationer). En almindelig kopi - ikke koblet sammen: rettes en plads senere, kopieres den
 * igen (eller med Excel: Åbningstider og helligdage). Kun materialer, målet har, kobles.
 */
const btnPrimary = "inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50";
const btnGhost = "inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 text-sm font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50";

export default function CalendarsCopy({ code, siblings = [], url = `${API}/logistics/calendars/copy`, onDone }) {
  const [open, setOpen] = useState(false);
  const [targets, setTargets] = useState([]);
  const [copying, setCopying] = useState(false);
  if (!siblings.length) return null;
  const copy = async () => {
    if (!targets.length) return;
    if (!window.confirm(`Kopiér kalenderne til ${targets.length} ${targets.length === 1 ? "plads" : "pladser"}?\n\nKalendere med samme navn på målet erstattes, og de materialer, målet har, får samme tilknytning. Det er en kopi: retter du kalenderen her senere, kopierer du den igen. Containere, materialer og bestillinger røres ikke.`)) return;
    setCopying(true);
    try {
      const { data } = await axios.post(url, { source_code: code, target_codes: targets });
      toast.success(`Kopieret til ${data.copied_to.join(", ")}`);
      // Materialer, målet ikke har, kobles ikke (de oprettes under Materialer og containere).
      if ((data.materials_missing || []).length) {
        toast.warning(`Ikke koblet (findes ikke på pladsen): ${data.materials_missing.map((m) => `${m.material} på ${m.sites.join(", ")}`).join("; ")}`, { duration: 9000 });
      }
      setOpen(false); setTargets([]);
      onDone && onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    setCopying(false);
  };
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3 space-y-2" data-testid={`calendars-copy-${code}`}>
      <button type="button" className={btnGhost} onClick={() => setOpen((v) => !v)} data-testid="calendars-copy-open">
        Kopiér kalenderne til andre pladser <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} /></button>
      {open && (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            {siblings.map((x) => (
              <label key={x.code} className="inline-flex items-center gap-1.5 text-sm px-2.5 py-1 rounded-lg border border-slate-200 dark:border-slate-600">
                <input type="checkbox" className="w-4 h-4 accent-emerald-600" checked={targets.includes(x.code)}
                  onChange={(e) => setTargets((t) => (e.target.checked ? [...t, x.code] : t.filter((c) => c !== x.code)))} data-testid="calendars-copy-target" />
                {x.name}
              </label>
            ))}
          </div>
          <p className="text-[11px] text-slate-500">Åbnings- og helligdagskalendere med primær og alternativer samt hvilke materialer de gælder for (kun materialer, pladsen har). En kalender med samme navn på målet erstattes. Det er en kopi - rettes kalenderen senere, kopieres den igen. Mange pladser på én gang: Excel (Åbningstider og helligdage). Containere, materialer og bestillinger røres ikke.</p>
          <button type="button" className={btnPrimary} disabled={!targets.length || copying} onClick={copy} data-testid="calendars-copy-run">
            {copying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Kopiér</button>
        </div>
      )}
    </div>
  );
}
